import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { ReduxState } from "model/state"
import { DEFAULT_DASHBOARD_ID } from "../context/dashboard-id"

// Client-side registry of which instances exist and how recently each was opened —
// NOT a layout contract type. CamelCase mirrors the resource DTO (DashboardInstance).
export interface InstanceState {
  instanceId: string
  name: string
  layoutId: string
  layoutVersionSeeded: number
  lastOpenedAt: string | null
}

// Flat map keyed by instanceId — instances are top-level (NOT nested by dashboardId).
// Partial forces TS to narrow absent keys to undefined.
export type InstancesRootState = Partial<Record<string, InstanceState>>

// Stable module-level fallbacks: a fresh `[]`/`{}` each render would churn selector
// consumers into re-render loops.
const EMPTY_INSTANCES: InstancesRootState = {}
const EMPTY_INSTANCE_LIST: InstanceState[] = []

function isSameInstance(a: InstanceState, b: InstanceState): boolean {
  return a.instanceId === b.instanceId
    && a.name === b.name
    && a.layoutId === b.layoutId
    && a.layoutVersionSeeded === b.layoutVersionSeeded
    && a.lastOpenedAt === b.lastOpenedAt
}

export const instancesSlice = createSlice({
  name: "dashboard/instances",
  initialState: {} as InstancesRootState,
  reducers: {
    // Upsert one instance. Skips no-op writes: Immer allocates a new draft even for
    // an identical write, which would fan a re-render across every selector consumer.
    upsertInstance(state, action: PayloadAction<InstanceState>) {
      const next = action.payload
      const existing = state[next.instanceId]
      if (existing && isSameInstance(existing, next)) return
      state[next.instanceId] = { ...next }
    },

    // Hydrate the whole registry from the fetched list. Skips the write when the
    // incoming set is identical (same ids + fields) to avoid spurious re-renders.
    setInstances(state, action: PayloadAction<InstanceState[]>) {
      const incoming = action.payload
      const existingIds = Object.keys(state)
      const unchanged =
        incoming.length === existingIds.length
        && incoming.every((entry) => {
          const existing = state[entry.instanceId]
          return existing != null && isSameInstance(existing, entry)
        })
      if (unchanged) return

      // Full replace — return a fresh map rather than mutating + deleting per key.
      return Object.fromEntries(incoming.map((entry) => [entry.instanceId, { ...entry }]))
    },

    // Removes the registry entry. The slice has no React unmount GC, so the caller
    // must explicitly remove or the entry leaks for the session.
    removeInstance(state, action: PayloadAction<string>) {
      const instanceId = action.payload
      if (state[instanceId] === undefined) return state
      // Full-replace style matching setInstances; the guard above keeps identity stable on no-op.
      return Object.fromEntries(
        Object.entries(state).filter(([key]) => key !== instanceId),
      )
    },
  },
})

function selectRoot(state: ReduxState): InstancesRootState {
  return state.dashboard.instances ?? EMPTY_INSTANCES
}

// Memoized so the array identity is stable across dispatches that don't touch the
// registry — a fresh array each call would re-render every useAppSelector consumer.
export const selectInstances = createSelector(
  [selectRoot],
  (root): InstanceState[] => {
    const ids = Object.keys(root)
    if (ids.length === 0) return EMPTY_INSTANCE_LIST
    return ids.map((id) => root[id] as InstanceState)
  },
)

export function selectInstanceById(state: ReduxState, instanceId: string): InstanceState | undefined {
  return selectRoot(state)[instanceId]
}

// Canonical recency order — lastOpenedAt desc, tiebreak instanceId asc, nulls (never opened) last.
// pickMostRecentInstanceId encodes the same rule, so the router's default landing dashboard is the
// most recently opened one. The switcher no longer sorts its tabs by this alone (orderInstances puts
// a saved manual order first), so the leftmost tab and the landing dashboard can differ.
export function compareByRecency(
  a: { instanceId: string, lastOpenedAt: string | null },
  b: { instanceId: string, lastOpenedAt: string | null },
): number {
  if (a.lastOpenedAt !== b.lastOpenedAt) {
    if (a.lastOpenedAt == null) return 1
    if (b.lastOpenedAt == null) return -1
    return a.lastOpenedAt < b.lastOpenedAt ? 1 : -1
  }
  return a.instanceId < b.instanceId ? -1 : 1
}

// Saved order first, then anything it does not mention, by recency among themselves. A saved id with
// no live instance yields no tab, so deletions leave no gap and a repeated id cannot render twice.
export function orderInstances<T extends { instanceId: string, lastOpenedAt: string | null }>(
  instances: readonly T[],
  savedOrder: readonly string[] | null | undefined,
): T[] {
  if (!savedOrder?.length) return [...instances].sort(compareByRecency)

  const unplaced = new Map(instances.map((instance) => [instance.instanceId, instance]))
  const ordered: T[] = []
  for (const instanceId of savedOrder) {
    const instance = unplaced.get(instanceId)
    if (instance === undefined) continue
    ordered.push(instance)
    unplaced.delete(instanceId)
  }

  return [...ordered, ...[...unplaced.values()].sort(compareByRecency)]
}

// "Most recent" = same ordering as compareByRecency (the switcher's leftmost tab), INCLUDING
// never-opened instances (null lastOpenedAt sorts last but stays eligible) — a non-empty list must
// resolve to a real dashboard, never the starter, so deleting the current one redirects to a survivor.
// Only a truly empty list → DEFAULT_DASHBOARD_ID. Allocation-free (no sort) for a synchronous pick.
export function pickMostRecentInstanceId(
  instances: readonly { instanceId: string, lastOpenedAt: string | null }[],
): string {
  let best: { instanceId: string, lastOpenedAt: string | null } | null = null
  for (const instance of instances) {
    if (best == null || compareByRecency(instance, best) < 0) best = instance
  }
  return best?.instanceId ?? DEFAULT_DASHBOARD_ID
}

export const selectMostRecentInstanceId = createSelector(
  [selectInstances],
  (instances): string => pickMostRecentInstanceId(instances),
)

export default instancesSlice.reducer
